import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/config';
import FeedbackButton from '../components/FeedbackButton';
import { checkAndRestoreActivityCache } from '../lib/claudeApi';

function SavedActivities() {
  const navigate = useNavigate();
  const [savedActivities, setSavedActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) {
      navigate('/');
      return;
    }
    fetch(`${API_BASE}/auth/activities/saved/${userId}`)
      .then(res => res.json())
      .then(data => {
        setSavedActivities(data.saved_activities || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [navigate]);

  async function handleSeizeActivities(item) {
    const userId = sessionStorage.getItem('carpeUserId');
    const destName = item.destination_name;
    const eventDate = item.event_date || null;
    const country = item.country || null;
    const lat = item.lat || null;
    const lng = item.lng || null;

    sessionStorage.setItem('carpeDestination', JSON.stringify({
      name: destName,
      country: country,
      event_date: eventDate,
      lat: lat,
      lng: lng,
    }));
    sessionStorage.setItem('carpeActivitiesReferrer', '/saved-activities');

    const restored = await checkAndRestoreActivityCache(userId, destName, eventDate);
    if (restored) {
      navigate('/activities');
      return;
    }

    // Cache miss — generate fresh instead of doing nothing.
    // Needs a real profile and country to build a valid request.
    const prof = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
    sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
      type: 'activities',
      endpoint: '/recommendations/activities',
      payload: {
        destination: destName,
        country: country,
        state_province: null,
        event_date: eventDate,
        lat: lat,
        lng: lng,
        profile: {
          profile: prof.profile,
          pace: prof.pace,
          activity_level: prof.activity_level,
          social_vibe: prof.social_vibe,
          explorer_score: prof.explorer_score,
          activity_profile: prof.activity_profile,
          activity_dimensions: prof.activity_dimensions,
          activity_profile_description: prof.activity_profile_description,
        },
        micro_answers: JSON.parse(sessionStorage.getItem('carpeMicroAnswers') || '{}'),
        teaser_activities: [],
      },
      destination: '/activities',
      loadingMessage: 'Finding the best experiences in ' + destName + '...',
      destinationFacts: [],
    }));
    navigate('/loading');
  }

  return (
    <div style={styles.page}>
      <FeedbackButton />
      <div style={styles.container}>
        <div style={styles.logo}>Carpe Wonder™</div>
        <button style={styles.backBtn} onClick={() => navigate('/welcome')}>
          ← Back
        </button>
        <h1 style={styles.headline}>My Saved Activities</h1>
        <p style={styles.subline}>Activities you have generated for each destination.</p>

        {loading ? (
          <div style={styles.empty}>Loading your saved activities...</div>
        ) : savedActivities.length === 0 ? (
          <div style={styles.empty}>
            No saved activities yet. Pick a destination and explore activities to save them.
          </div>
        ) : (
          savedActivities.map((item, i) => (
            <div key={i} style={styles.card}>
              <div style={styles.destName}>{item.destination_name}</div>
              <div style={styles.date}>
                Saved {new Date(item.generated_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {item.event_date && ` — for ${new Date(item.event_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}`}
              </div>
              <button
                style={styles.seizeBtn}
                onClick={() => handleSeizeActivities(item)}
              >
                See activities →
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 16px',
  },
  container: {
    width: '100%',
    maxWidth: '480px',
  },
  logo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: '16px',
    textAlign: 'center',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '0',
    marginBottom: '24px',
  },
  headline: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#fff',
    marginBottom: '8px',
  },
  subline: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '24px',
  },
  empty: {
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
    padding: '40px 0',
  },
  card: {
    background: '#1a1a35',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '16px',
    border: '0.5px solid rgba(255,255,255,0.08)',
  },
  destName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
    marginBottom: '4px',
  },
  date: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.4)',
    marginBottom: '16px',
  },
  seizeBtn: {
    width: '100%',
    background: 'transparent',
    color: 'var(--gold)',
    border: '1px solid rgba(201,168,76,0.4)',
    borderRadius: '28px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default SavedActivities;
